import {
  Box,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { PiInfo } from 'react-icons/pi';
import { formatPrice } from '@/lib/helpers';
import { PaymentMethod } from '@/types';
import { InvoiceTotals } from '../hooks/useInvoiceCalculation';

interface TotalsSectionProps {
  totals: InvoiceTotals;
  invoiceCurrency: string;
  paymentMethod: PaymentMethod;
  vatExplanation?: string;
}

export default function TotalsSection({
  totals,
  invoiceCurrency,
  paymentMethod,
  vatExplanation,
}: TotalsSectionProps) {
  const isCrypto = paymentMethod === PaymentMethod.Cryptocurrency;
  const vatPercentage = (totals.vatRate * 100).toFixed(0);

  // Check if there are any currency conversions
  const hasConversions = totals.currencySubtotals.some(
    (cs) => cs.currency !== invoiceCurrency,
  );

  return (
    <Paper p="md" withBorder>
      <Text size="xs" fw={600} c="dimmed" mb="md" tt="uppercase">
        Totals
      </Text>

      <Stack gap="xs">
        {/* Show subtotals by currency if there are conversions */}
        {hasConversions && (
          <>
            {totals.currencySubtotals.map((cs) => (
              <Group key={cs.currency} justify="space-between">
                <Text size="sm" c="dimmed">
                  Subtotal ({cs.currency})
                </Text>
                <Stack gap={0} align="flex-end">
                  <Text size="sm">{formatPrice(cs.amount, cs.currency)}</Text>
                  {cs.currency !== invoiceCurrency && (
                    <Text size="xs" c="dimmed">
                      (1 {cs.currency} = {cs.conversionRate.toFixed(4)}{' '}
                      {invoiceCurrency})
                    </Text>
                  )}
                </Stack>
              </Group>
            ))}
            <Divider />
          </>
        )}

        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Subtotal ({invoiceCurrency})
          </Text>
          <Text size="sm">{formatPrice(totals.subtotal, invoiceCurrency)}</Text>
        </Group>

        <Group justify="space-between">
          <Group gap={4}>
            <Text size="sm" c="dimmed">
              VAT ({vatPercentage}%)
            </Text>
            {vatExplanation && (
              <Tooltip label={vatExplanation} multiline w={250}>
                <Box style={{ cursor: 'help' }}>
                  <PiInfo size={14} color="var(--mantine-color-gray-5)" />
                </Box>
              </Tooltip>
            )}
          </Group>
          <Text size="sm">
            {formatPrice(totals.vatAmount, invoiceCurrency)}
          </Text>
        </Group>

        <Divider />

        <Group justify="space-between">
          <Text size="sm" fw={500}>
            Total ({invoiceCurrency})
          </Text>
          <Text size="sm" fw={500}>
            {formatPrice(totals.total, invoiceCurrency)}
          </Text>
        </Group>

        {isCrypto && (
          <>
            <Divider my="xs" />

            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Crypto Conversion
            </Text>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Subtotal (USD)
              </Text>
              <Text size="sm">
                {formatPrice(totals.total * (totals.exchangeRate || 1), 'USD')}
              </Text>
            </Group>

            <Group justify="space-between">
              <Tooltip label="Fee charged for converting cryptocurrency to fiat currency">
                <Group gap={4} style={{ cursor: 'help' }}>
                  <Text size="sm" c="dimmed">
                    Off-ramping fee (
                    {((totals.offRampingFeeRate ?? 0.03) * 100).toFixed(0)}%)
                  </Text>
                  <PiInfo size={14} color="var(--mantine-color-gray-5)" />
                </Group>
              </Tooltip>
              <Text size="sm">
                {formatPrice(totals.offRampingFee || 0, 'USD')}
              </Text>
            </Group>

            <Divider />

            <Group justify="space-between">
              <Text size="sm" fw={600}>
                Amount Due (USD)
              </Text>
              <Text size="lg" fw={600} c="blue">
                {formatPrice(totals.dueAmount, 'USD')}
              </Text>
            </Group>

            {invoiceCurrency !== 'USD' && totals.exchangeRate && (
              <Text size="xs" c="dimmed" ta="right">
                (1 {invoiceCurrency} = {totals.exchangeRate.toFixed(4)} USD)
              </Text>
            )}
          </>
        )}

        {!isCrypto && (
          <Group justify="space-between" mt="xs">
            <Text size="sm" fw={600}>
              Amount Due
            </Text>
            <Text size="lg" fw={600} c="blue">
              {formatPrice(totals.dueAmount, totals.dueCurrency)}
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
